/**
 * By Pharmacy Tab → Invoice Form Item Mapper
 *
 * Maps prescription items returned by the `invoice-details-by-items` API
 * into the form item structure used by the invoice form.
 *
 * Uses the same financial calculation logic as the Pharmacy Dispense flow
 * (calcFinancials / buildTaxesArray) so that batch, tax, and pricing
 * behaviour is consistent across both flows.
 *
 * This file is used ONLY for items arriving from the "By Pharmacy" tab.
 * The existing appointment-based prescription mapping in NewPharmacyForm.tsx
 * (mapPrescriptionToFormItem) is NOT affected.
 */

import {
  PharmacyItem as FormPharmacyItem,
  PrescriptionItem,
} from './newPharmacyForm.helper';
import { calcFinancials, buildTaxesArray } from '../../../pharmacy-dispense/directDispenseFormHelpers';
import { formatDateDMY } from '../../../../utils/dateUtils';
import { PatientDrug } from '../../Invoice/invoiceHelper';

/**
 * Map a PrescriptionItem (from invoice-details-by-items API) to the invoice
 * form item structure, with full batch / tax / pricing data — matching the
 * pharmacy dispense flow.
 */
export const mapSelectedPrescriptionToFormItem = (
  prescription: PrescriptionItem
): FormPharmacyItem => {
  // --- map available batches ------------------------------------------------
  const availableBatches = (prescription.available_batches || []).map((batch) => {
    const batchMrp = batch.base_mrp ?? 0;
    const batchTaxesArray = buildTaxesArray(
      batch.taxes as unknown as { [key: string]: { percent?: number; amount?: number } },
      batchMrp
    );

    return {
      batchId: batch.id ?? batch.batch_id,
      batchNumber: batch.batch_number,
      expiryDate: formatDateDMY(batch.expiry_date),
      isExpired: batch.is_expired,
      baseMrp: batch.base_mrp,
      baseCostPrice: batch.base_cost_price ?? batch.base_cost ?? null,
      availableStock: batch.available_stock,
      availableUomId: batch.available_uom_id,
      availableUom: batch.available_uom || batch.available_uom_name,
      storeId: batch.store_id,
      taxes: batchTaxesArray,
    };
  });

  const billedQty = prescription.remaining_qty || prescription.billed_qty || 0;

  // --- package details ------------------------------------------------------
  const packageDetail = prescription.package_details?.[0];
  const isFromPackage = !!packageDetail;

  // --- select batch (match by batch_code, fallback to first) ----------------
  const selectedBatch = prescription.batch_code
    ? availableBatches.find((b) => b.batchNumber === prescription.batch_code) ??
      availableBatches[0]
    : availableBatches[0];

  const baseMRP = selectedBatch?.baseMrp ?? 0;
  const baseCostPrice = selectedBatch?.baseCostPrice ?? 0;
  const totalMRPAmount = baseMRP * billedQty;

  // --- financial calculations (dispense-style) ------------------------------
  const baseTaxesArray =
    selectedBatch?.taxes && selectedBatch.taxes.length > 0
      ? selectedBatch.taxes
      : [];

  const discountPercent = packageDetail?.discount_percent ?? 0;

  const itemTaxes = baseTaxesArray.map((t) => ({
    tax_name: t.name,
    tax_percentage: t.percent,
  }));

  const financials = calcFinancials(baseMRP, billedQty, discountPercent, itemTaxes);

  const mappedTaxes = financials.taxesWithAmounts.map((t) => ({
    name: t.tax_name,
    percent: t.tax_percentage,
    amount: t.tax_amount,
  }));

  // --- build form item ------------------------------------------------------
  return {
    prescriptionId: prescription.prescription_id,
    itemId: prescription.item_id,
    visitDate: formatDateDMY(prescription.visit_date),
    itemCode: prescription.item_code,
    itemName: prescription.item_name,
    batchCode: selectedBatch?.batchNumber ?? prescription.batch_code ?? '',
    batchId: selectedBatch?.batchId,
    expiryDate: selectedBatch?.expiryDate ?? formatDateDMY(prescription.expiry_date),
    manufacturer: prescription.manufacturer || '',
    availableBatches,
    prescribedStrength: prescription.prescribed_strength || prescription.strength || '',
    prescribedQty: prescription.prescribed_qty ?? prescription.prescribed_quantity ?? 0,
    prescribedUOM: prescription.prescribed_uom,
    billedStrength: prescription.strength || '',
    billedQty,
    billedUOM: selectedBatch?.availableUom ?? prescription.billed_uom_code ?? '',
    rack: prescription.rack ?? '',
    shelf: prescription.shelf ?? '',
    container: prescription.container ?? '',
    checked: true,
    baseMRP,
    baseCostPrice,
    totalMRPAmount,
    sellingPricePerUnit: financials.sellingPricePerUnit,
    totalSellingPrice: financials.taxableAmount,
    concessionAmount: financials.discount,
    concessionPercent: discountPercent,
    taxes: mappedTaxes,
    netAmount: financials.netAmount,
    pregnancyClass: prescription.pregnancy_class_code ?? '',
    provider: prescription.prescribed_by || '',
    availableStock: selectedBatch?.availableStock ?? prescription.available_stock,
    availableUomId: selectedBatch?.availableUomId ?? prescription.available_uom_id,
    availableUom: selectedBatch?.availableUom ?? prescription.available_uom,
    storeId: selectedBatch?.storeId,
    tariffId: null,
    tariffName: undefined,
    packageId: packageDetail?.package_id ?? null,
    packageName: packageDetail?.package_name,
    prescribed_remaining_qty: prescription.remaining_qty,
    uomConversions: prescription.uom_conversions || [],
    is_fromPackage: isFromPackage,
    package_pharmacy_item_id: packageDetail?.package_pharmacy_item_id ?? undefined,
    package_total_qty: packageDetail?.total_quantity ?? undefined,
    package_pending_qty: packageDetail?.quantity_remaining ?? undefined,
    package_pharmacy_remaining_amount: packageDetail?.pharmacy_remaining_amount,
    package_unit_price: packageDetail?.mrp ?? baseMRP,
    uom_description: packageDetail?.uom_description ?? undefined,
    package_details: prescription.package_details || [],
    // Mark as dispense item - concession should be disabled
    is_from_dispense: true,
  };
};

/**
 * Fallback mapper — maps a PatientDrug (from the patient-drugs API) to a
 * basic form item WITHOUT batch / pricing data.  Used only when the
 * invoice-details-by-items API returns no items.
 */
export const mapPatientDrugToFormItem = (drug: PatientDrug): FormPharmacyItem => ({
  prescriptionId: drug.id,
  itemId: drug.drug.id,
  visitDate: drug.created_on ? formatDateDMY(drug.created_on) : '',
  itemCode: '',
  itemName: drug.drug.name || drug.drug.name_label || '',
  batchCode: '',
  expiryDate: '',
  manufacturer: '',
  availableBatches: [],
  prescribedStrength: drug.drug.strength || '',
  prescribedQty: drug.quantity ?? 0,
  prescribedUOM: drug.drug.stock_uom_code || drug.drug.base_uom_code || '',
  billedStrength: drug.drug.strength || '',
  billedQty: drug.quantity ?? 0,
  billedUOM: drug.drug.stock_uom_code || drug.drug.base_uom_code || '',
  frequency: (drug.frequency as FormPharmacyItem['frequency']) || undefined,
  rack: '',
  shelf: '',
  container: '',
  checked: true,
  baseMRP: 0,
  baseCostPrice: 0,
  totalMRPAmount: 0,
  concessionAmount: 0,
  concessionPercent: 0,
  taxes: [],
  netAmount: 0,
  pregnancyClass: '',
  provider: drug.user_full_name || '',
  prescribed_remaining_qty: drug.remaining_quantity ?? drug.quantity,
});
